import { useState, useCallback, useRef, useEffect } from 'react';
import statsApi from '../api/statsApi';
import { buildStatsSnapshotParams } from '../utils/statsFilterUtils';
import { mapSnapshotToLegacyDashboard } from '../utils/statsSnapshotAdapter';

/**
 * Statistics Data Fetch Hook with Request Deduplication
 * Prevents duplicate requests and manages loading/error states
 * 
 * @param {string} activeTab - Current active tab
 * @param {Object} appliedFilters - Applied filter values
 * @param {Object} options - Additional options
 * @returns {Object} Data, loading, error states and refetch function
 */
export function useStatsDataFetch(activeTab, appliedFilters, options = {}) {
  const { enabled = true, onSuccess, onError } = options;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Request deduplication
  const lastRequestRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isFetchingRef = useRef(false);

  /**
   * Get API method for current tab
   */
  const getApiMethod = useCallback((tab) => {
    switch (tab) {
      case 'loads':
        return statsApi.getLoadsStats;
      case 'payments':
        return statsApi.getPaymentsStats;
      case 'carriers':
        return statsApi.getCarriersStats;
      case 'customers':
        return statsApi.getCustomersStats;
      case 'receivable':
        return (params) => statsApi.getPaymentsStats({ ...params, paymentType: 'receivable' });
      case 'payable':
        return (params) => statsApi.getPaymentsStats({ ...params, paymentType: 'payable' });
      case 'users':
        // If users endpoint exists, use it; otherwise fallback to dashboard
        return statsApi.getDashboard;
      case 'overview':
      default:
        return statsApi.getDashboard;
    }
  }, []);

  /**
   * Fetch statistics data
   */
  const fetchData = useCallback(async (tab, filters, force = false) => {
    // Skip if disabled
    if (!enabled) return;

    // Build request signature for deduplication
    const requestSignature = JSON.stringify({
      tab,
      filters: JSON.stringify(filters)
    });

    // Skip if same request is already in progress (unless forced)
    if (!force && isFetchingRef.current && lastRequestRef.current === requestSignature) {
      return;
    }

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();
    isFetchingRef.current = true;
    lastRequestRef.current = requestSignature;

    setLoading(true);
    setError(null);

    try {
      const params = buildStatsSnapshotParams(filters, tab);
      const apiMethod = getApiMethod(tab);
      const response = await apiMethod(params);

      if (abortControllerRef.current?.signal.aborted) {
        return;
      }

      if (response?.data?.success && response?.data?.data) {
        const raw = response.data.data;
        const mapped = mapSnapshotToLegacyDashboard(raw) ?? raw;
        setData(mapped);
        onSuccess?.(mapped);
      } else {
        throw new Error(response?.data?.error || 'Failed to load statistics');
      }
    } catch (err) {
      // Ignore abort errors
      if (err.name === 'AbortError') {
        return;
      }

      console.error('[useStatsDataFetch] Error fetching statistics:', err);
      const errorMessage = err.message || 'Failed to load statistics';
      setError(errorMessage);
      onError?.(err);
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
    }
  }, [enabled, getApiMethod, onSuccess, onError]);

  /**
   * Refetch current data
   */
  const refetch = useCallback((force = false) => {
    return fetchData(activeTab, appliedFilters, force);
  }, [activeTab, appliedFilters, fetchData]);

  // Auto-fetch when tab or filters change
  useEffect(() => {
    if (!enabled) return;

    // Build request signature for comparison
    const requestSignature = JSON.stringify({
      tab: activeTab,
      filters: JSON.stringify(appliedFilters)
    });

    // Skip if same request is already in progress
    if (isFetchingRef.current && lastRequestRef.current === requestSignature) {
      return;
    }

    fetchData(activeTab, appliedFilters);

    // Cleanup: abort request on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, appliedFilters, enabled]);

  return {
    data,
    loading,
    error,
    refetch
  };
}
